import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UUID, WorkScheduleDto } from '@4zen/api-client';
import { api } from '@/lib/api';

export function useUsers(query?: { search?: string; include_inactive?: boolean }) {
  return useQuery({
    queryKey: ['users', query ?? {}],
    queryFn: () => api.users.list({ limit: 200, ...query }),
    staleTime: 5 * 60_000,
  });
}

export function useGroups(enabled = true) {
  return useQuery({
    queryKey: ['groups'],
    queryFn: () => api.users.groups(),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useSchedule(userId: UUID | undefined) {
  return useQuery({
    queryKey: ['schedule', userId],
    queryFn: () => api.users.schedule(userId!),
    enabled: Boolean(userId),
  });
}

export function useUpdateSchedule(userId: UUID) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<WorkScheduleDto>) => api.users.updateSchedule(userId, body),
    onSuccess: (schedule) => {
      queryClient.setQueryData(['schedule', userId], schedule);
      void queryClient.invalidateQueries({ queryKey: ['smart-flow'] });
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => api.users.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: UUID; body: Record<string, unknown> }) =>
      api.users.update(id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}
